import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Formik } from 'formik';
import * as FeatherIcons from 'react-icons/fi';
import {
  FiAlertTriangle,
  FiEdit2,
  FiInfo,
  FiPlus,
  FiTag,
  FiTrash2,
} from 'react-icons/fi';

import BasePage from '../../../../components/BasePage';
import DataTable, { Column } from '../../../../components/DataTable';
import { useToast } from '../../../../hooks/useToast';
import api from '../../../../services/api';

import icons from './icons';

import {
  CategoryCell,
  CreateButton,
  EmptyText,
  IconButton,
  IconPreview,
  MutedText,
} from './styles';
import {
  DangerAlert,
  FieldBlock,
  FieldHint,
  FooterButton,
  FooterHint,
  Label,
  ModalSubtitle,
  Section,
  SectionHead,
  Select,
  TextArea,
  TextInput,
} from './modalStyles';

interface ICategory {
  id: string;
  title: string;
  icon: string;
  description: string;
}

const renderIcon = (name: string, size = 18) => {
  const Component = (
    FeatherIcons as Record<string, React.ComponentType<{ size?: number }>>
  )[name];
  if (!Component) return null;
  return <Component size={size} />;
};

const PartnersCategories: React.FC = () => {
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<ICategory[]>([]);

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [selectedId, setSelectedId] = useState('');
  const [editCategory, setEditCategory] = useState<ICategory | undefined>();

  useEffect(() => {
    document.title = 'Categorias de Convênios - Viva Voz';
  }, []);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<ICategory[]>(
        '/unions-partners/categories',
      );
      setCategories(response.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleOpenEdit = useCallback(async (id: string) => {
    const response = await api.get<ICategory>(
      `/unions-partners/categories/${id}`,
    );
    setSelectedId(id);
    setEditCategory(response.data);
    setShowEdit(true);
  }, []);

  const handleOpenDelete = useCallback((id: string) => {
    setSelectedId(id);
    setShowDelete(true);
  }, []);

  const handleCreateCategory = useCallback(
    async (values, { resetForm }) => {
      try {
        await api.post('/unions-partners/categories', values);
        await loadCategories();
        addToast({
          title: 'Sucesso',
          body: 'Categoria criada com sucesso.',
        });
        resetForm();
        setShowAdd(false);
      } catch (error) {
        addToast({
          title: 'Erro',
          body: 'Ocorreu um erro ao tentar adicionar uma nova categoria.',
        });
      }
    },
    [addToast, loadCategories],
  );

  const handleUpdateCategory = useCallback(
    async values => {
      try {
        await api.patch(`/unions-partners/categories/${selectedId}`, values);
        await loadCategories();
        addToast({
          title: 'Sucesso',
          body: 'Categoria atualizada com sucesso.',
        });
        setShowEdit(false);
      } catch (error) {
        addToast({
          title: 'Erro',
          body: 'Ocorreu um erro ao tentar atualizar a categoria.',
        });
      }
    },
    [addToast, loadCategories, selectedId],
  );

  const handleDeleteCategory = useCallback(async () => {
    try {
      await api.delete(`/unions-partners/categories/${selectedId}`);
      await loadCategories();
      addToast({
        title: 'Sucesso',
        body: 'Categoria removida com sucesso.',
      });
      setShowDelete(false);
    } catch (error) {
      addToast({
        title: 'Erro',
        body: 'Ocorreu um erro ao tentar remover a categoria.',
      });
    }
  }, [addToast, loadCategories, selectedId]);

  const columns = useMemo<Column<ICategory>[]>(
    () => [
      {
        key: 'title',
        label: 'Nome',
        width: '32%',
        render: row => (
          <CategoryCell>
            <span className="icon">
              {row.icon ? renderIcon(row.icon, 18) : <FiTag size={18} />}
            </span>
            <span className="title" title={row.title}>
              {row.title}
            </span>
          </CategoryCell>
        ),
      },
      {
        key: 'description',
        label: 'Descrição',
        render: row =>
          row.description ? (
            <MutedText title={row.description}>{row.description}</MutedText>
          ) : (
            <EmptyText>Sem descrição</EmptyText>
          ),
      },
    ],
    [],
  );

  return (
    <>
      <BasePage title="Categorias de Convênios">
        <DataTable<ICategory>
          title="Categorias de convênios"
          subtitle="Organize os convênios por categorias para facilitar a navegação dos associados."
          columns={columns}
          data={categories}
          rowKey="id"
          loading={loading}
          page={1}
          pageSize={categories.length || 1}
          total={categories.length}
          onPageChange={() => null}
          headerActions={(
            <CreateButton type="button" onClick={() => setShowAdd(true)}>
              <FiPlus size={14} />
              Nova categoria
            </CreateButton>
          )}
          rowActions={row => (
            <>
              <IconButton
                type="button"
                title="Editar"
                onClick={() => handleOpenEdit(row.id)}
              >
                <FiEdit2 size={14} />
              </IconButton>
              <IconButton
                type="button"
                title="Remover"
                $danger
                onClick={() => handleOpenDelete(row.id)}
              >
                <FiTrash2 size={14} />
              </IconButton>
            </>
          )}
          emptyMessage="Nenhuma categoria cadastrada."
        />
      </BasePage>

      {/* Add Category Modal */}
      <Modal
        show={showAdd}
        onHide={() => setShowAdd(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        size="lg"
      >
        <Formik
          initialValues={{ title: '', icon: '', description: '' }}
          onSubmit={handleCreateCategory}
        >
          {({ values, handleSubmit, handleChange }) => (
            <form onSubmit={handleSubmit}>
              <Modal.Header closeButton>
                <div>
                  <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>
                    Nova categoria
                  </Modal.Title>
                  <ModalSubtitle>
                    Cadastre uma categoria para agrupar os convênios disponíveis
                    aos associados.
                  </ModalSubtitle>
                </div>
              </Modal.Header>

              <Modal.Body style={{ background: '#fafbfc', padding: 20 }}>
                <Section>
                  <SectionHead>
                    <span className="badge">
                      <FiTag size={16} />
                    </span>
                    <div>
                      <h4>Dados da categoria</h4>
                      <span className="subtitle">Nome, ícone e descrição</span>
                    </div>
                  </SectionHead>

                  <FieldBlock>
                    <Label>Nome *</Label>
                    <TextInput
                      type="text"
                      placeholder="Ex.: Saúde, Educação, Lazer"
                      value={values.title}
                      onChange={handleChange('title')}
                    />
                    <FieldHint>
                      Nome que aparecerá no sistema para os associados.
                    </FieldHint>
                  </FieldBlock>

                  <FieldBlock>
                    <Label>Ícone</Label>
                    <Select value={values.icon} onChange={handleChange('icon')}>
                      <option value="">Nenhum</option>
                      {icons.map(icon => (
                        <option key={icon} value={icon}>
                          {icon}
                        </option>
                      ))}
                    </Select>
                    {values.icon && (
                      <IconPreview>
                        <span className="glyph">
                          {renderIcon(values.icon, 18)}
                        </span>
                        <span>Pré-visualização do ícone selecionado.</span>
                      </IconPreview>
                    )}
                  </FieldBlock>

                  <FieldBlock>
                    <Label>Descrição</Label>
                    <TextArea
                      rows={3}
                      placeholder="Breve descrição do que esta categoria representa"
                      value={values.description}
                      onChange={handleChange('description')}
                    />
                    <FieldHint>
                      Ajuda os associados a entenderem o conteúdo da categoria.
                    </FieldHint>
                  </FieldBlock>
                </Section>

                <FooterHint>
                  <FiInfo size={13} />
                  <span>
                    Campos com * são obrigatórios. Você pode editar a categoria
                    a qualquer momento.
                  </span>
                </FooterHint>
              </Modal.Body>

              <Modal.Footer>
                <FooterButton
                  type="button"
                  $variant="secondary"
                  onClick={() => setShowAdd(false)}
                >
                  Cancelar
                </FooterButton>
                <FooterButton type="submit" disabled={!values.title.trim()}>
                  Salvar categoria
                </FooterButton>
              </Modal.Footer>
            </form>
          )}
        </Formik>
      </Modal>

      {/* Edit Category Modal */}
      <Modal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        size="lg"
      >
        {editCategory && (
          <Formik
            enableReinitialize
            initialValues={{
              title: editCategory.title || '',
              icon: editCategory.icon || '',
              description: editCategory.description || '',
            }}
            onSubmit={handleUpdateCategory}
          >
            {({ values, handleSubmit, handleChange }) => (
              <form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                  <div>
                    <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>
                      Editar categoria
                    </Modal.Title>
                    <ModalSubtitle>
                      Atualize o nome, o ícone ou a descrição desta categoria.
                    </ModalSubtitle>
                  </div>
                </Modal.Header>

                <Modal.Body style={{ background: '#fafbfc', padding: 20 }}>
                  <Section>
                    <SectionHead>
                      <span className="badge">
                        <FiTag size={16} />
                      </span>
                      <div>
                        <h4>Dados da categoria</h4>
                        <span className="subtitle">
                          Nome, ícone e descrição
                        </span>
                      </div>
                    </SectionHead>

                    <FieldBlock>
                      <Label>Nome *</Label>
                      <TextInput
                        type="text"
                        placeholder="Ex.: Saúde, Educação, Lazer"
                        value={values.title}
                        onChange={handleChange('title')}
                      />
                      <FieldHint>
                        Nome que aparecerá no sistema para os associados.
                      </FieldHint>
                    </FieldBlock>

                    <FieldBlock>
                      <Label>Ícone</Label>
                      <Select
                        value={values.icon}
                        onChange={handleChange('icon')}
                      >
                        <option value="">Nenhum</option>
                        {icons.map(icon => (
                          <option key={icon} value={icon}>
                            {icon}
                          </option>
                        ))}
                      </Select>
                      {values.icon && (
                        <IconPreview>
                          <span className="glyph">
                            {renderIcon(values.icon, 18)}
                          </span>
                          <span>Pré-visualização do ícone selecionado.</span>
                        </IconPreview>
                      )}
                    </FieldBlock>

                    <FieldBlock>
                      <Label>Descrição</Label>
                      <TextArea
                        rows={3}
                        placeholder="Breve descrição do que esta categoria representa"
                        value={values.description}
                        onChange={handleChange('description')}
                      />
                      <FieldHint>
                        Ajuda os associados a entenderem o conteúdo da
                        categoria.
                      </FieldHint>
                    </FieldBlock>
                  </Section>
                </Modal.Body>

                <Modal.Footer>
                  <FooterButton
                    type="button"
                    $variant="secondary"
                    onClick={() => setShowEdit(false)}
                  >
                    Cancelar
                  </FooterButton>
                  <FooterButton type="submit" disabled={!values.title.trim()}>
                    Salvar alterações
                  </FooterButton>
                </Modal.Footer>
              </form>
            )}
          </Formik>
        )}
      </Modal>

      {/* Delete Category Modal */}
      <Modal
        show={showDelete}
        onHide={() => setShowDelete(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>
              Remover categoria
            </Modal.Title>
            <ModalSubtitle>
              Confirme a remoção desta categoria do sistema.
            </ModalSubtitle>
          </div>
        </Modal.Header>
        <Modal.Body style={{ padding: 20 }}>
          <DangerAlert>
            <FiAlertTriangle size={18} />
            <span>
              Você realmente deseja remover esta categoria? Essa ação não poderá
              ser desfeita e os convênios vinculados ficarão sem categoria.
            </span>
          </DangerAlert>
        </Modal.Body>
        <Modal.Footer>
          <FooterButton
            type="button"
            $variant="secondary"
            onClick={() => setShowDelete(false)}
          >
            Cancelar
          </FooterButton>
          <FooterButton
            type="button"
            $variant="danger"
            onClick={handleDeleteCategory}
          >
            Remover categoria
          </FooterButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PartnersCategories;
